import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import {
  Settings,
  Sun,
  Moon,
  Laptop,
  History,
  Database,
  AlertTriangle,
  Trash2,
  Loader2,
  ChevronRight,
  KeyRound,
  Users,
  Scissors,
  Save,
  X,
  Calculator,
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";

const THEMES = [
  { key: "light", label: "Claro", icon: Sun },
  { key: "dark", label: "Escuro", icon: Moon },
  { key: "system", label: "Automático", icon: Laptop },
];

export default function Configuracoes() {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();
  const [limparOpen, setLimparOpen] = useState(false);
  const [limparTexto, setLimparTexto] = useState("");
  const [saving, setSaving] = useState(false);
  const [tiposPedaco, setTiposPedaco] = useState([]);
  const [loadingTipos, setLoadingTipos] = useState(false);
  const [rascunhos, setRascunhos] = useState({}); // { tipo: 'valor' }
  const [savingTipo, setSavingTipo] = useState("");

  // Rendimento de gola/punho — cartão único fixo
  const [rendRasc, setRendRasc] = useState({
    juntos_golas: "",
    juntos_punhos: "",
    gola_sep: "",
    punho_sep: "",
  });
  const [rendMeta, setRendMeta] = useState({ updated_by_nome: null, updated_at: null });
  const [loadingRend, setLoadingRend] = useState(false);
  const [savingRend, setSavingRend] = useState(false);

  useEffect(() => {
    if (!user?.is_master) return;
    setLoadingTipos(true);
    api.get("/tipos-pedaco/registry")
      .then((r) => {
        setTiposPedaco(r.data);
        const rasc = {};
        (r.data || []).forEach((t) => { rasc[t.tipo] = t.limite != null ? String(t.limite).replace(".", ",") : ""; });
        setRascunhos(rasc);
      })
      .catch((e) => { const _m = apiError(e); if (_m) toast.error(_m); })
      .finally(() => setLoadingTipos(false));
  }, [user?.is_master]);

  const salvarLimite = async (tipo) => {
    setSavingTipo(tipo);
    try {
      const raw = String(rascunhos[tipo] ?? "").replace(",", ".").trim();
      const payload = raw === "" ? { limite: null } : { limite: parseFloat(raw) };
      if (raw !== "" && (Number.isNaN(payload.limite) || payload.limite <= 0)) {
        toast.error("Informe um número maior que zero");
        return;
      }
      await api.put(`/tipos-pedaco/${encodeURIComponent(tipo)}`, payload);
      const r = await api.get("/tipos-pedaco/registry");
      setTiposPedaco(r.data);
      toast.success(raw === "" ? `Limite removido de ${tipo}` : `Limite de ${tipo} salvo: ${raw}`);
    } catch (e) {
      const _m = apiError(e); if (_m) toast.error(_m);
    } finally {
      setSavingTipo("");
    }
  };

  const limparLimite = async (tipo) => {
    setRascunhos((prev) => ({ ...prev, [tipo]: "" }));
    setSavingTipo(tipo);
    try {
      await api.put(`/tipos-pedaco/${encodeURIComponent(tipo)}`, { limite: null });
      const r = await api.get("/tipos-pedaco/registry");
      setTiposPedaco(r.data);
      toast.success(`Limite removido de ${tipo}`);
    } catch (e) {
      const _m = apiError(e); if (_m) toast.error(_m);
    } finally {
      setSavingTipo("");
    }
  };

  // ── Rendimento gola/punho: carregar + salvar ─────────────────────
  const carregarRendimento = async () => {
    setLoadingRend(true);
    try {
      const r = await api.get("/rendimento-gola-punho");
      const d = r.data || {};
      const fmt = (v) => (v != null ? String(v).replace(".", ",") : "");
      setRendRasc({
        juntos_golas: fmt(d.juntos_golas),
        juntos_punhos: fmt(d.juntos_punhos),
        gola_sep: fmt(d.gola_sep),
        punho_sep: fmt(d.punho_sep),
      });
      setRendMeta({ updated_by_nome: d.updated_by_nome, updated_at: d.updated_at });
    } catch (e) {
      const _m = apiError(e); if (_m) toast.error(_m);
    } finally {
      setLoadingRend(false);
    }
  };

  useEffect(() => {
    if (!user?.is_master) return;
    carregarRendimento();
  }, [user?.is_master]);

  const parseRendValor = (v) => {
    const raw = String(v ?? "").replace(",", ".").trim();
    if (raw === "") return null;
    const n = parseFloat(raw);
    if (Number.isNaN(n) || n <= 0) return "ERR";
    return n;
  };

  const salvarRendimento = async () => {
    const jg = parseRendValor(rendRasc.juntos_golas);
    const jp = parseRendValor(rendRasc.juntos_punhos);
    const gs = parseRendValor(rendRasc.gola_sep);
    const ps = parseRendValor(rendRasc.punho_sep);
    if ([jg, jp, gs, ps].includes("ERR")) {
      toast.error("Os rendimentos devem ser números maiores que zero (ou vazios).");
      return;
    }
    setSavingRend(true);
    try {
      await api.put("/rendimento-gola-punho", {
        juntos_golas: jg,
        juntos_punhos: jp,
        gola_sep: gs,
        punho_sep: ps,
      });
      await carregarRendimento();
      toast.success("Rendimento salvo");
    } catch (e) {
      const _m = apiError(e); if (_m) toast.error(_m);
    } finally {
      setSavingRend(false);
    }
  };

  const doLimpar = async () => {
    setSaving(true);
    try {
      const res = await api.post("/admin/limpar-dados-teste", { confirmar: limparTexto });
      const r = res.data.removidos;
      toast.success(
        `Dados de teste limpos: ${r.rolos} rolos, ${r.combinacoes} tecidos, ${r.fornecedores} fornecedores`
      );
      setLimparOpen(false);
      setLimparTexto("");
    } catch (e) {
      {const _m=apiError(e); if(_m) toast.error(_m);};
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "h-11 w-full rounded-md border-2 border-slate-300 px-3 text-base outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200";

  return (
    <div data-testid="configuracoes-page" className="mx-auto max-w-3xl">
      <div className="mb-6">
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
          <Settings size={28} /> Configurações
        </h1>
        <p className="mt-1 text-sm text-slate-500">Aparência, histórico e administração de dados.</p>
      </div>

      {/* Aparência */}
      <section className="rounded-md border-2 border-slate-200 bg-white p-5" data-testid="config-aparencia">
        <h2 className="text-lg font-bold text-slate-900">Aparência</h2>
        <p className="mt-1 text-sm text-slate-500">
          Escolha o tema do sistema. "Automático" segue a configuração do seu aparelho.
        </p>
        <div className="mt-4 grid grid-cols-3 gap-3">
          {THEMES.map((t) => (
            <button
              key={t.key}
              onClick={() => setTheme(t.key)}
              data-testid={`tema-${t.key}`}
              className={`flex flex-col items-center gap-2 rounded-md border-2 px-3 py-4 text-sm font-semibold transition-colors ${
                theme === t.key
                  ? "border-blue-500 bg-blue-50 text-blue-700"
                  : "border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              <t.icon size={22} /> {t.label}
            </button>
          ))}
        </div>
      </section>

      {/* Histórico — só master */}
      {user?.is_master && (
      <section className="mt-6 rounded-md border-2 border-slate-200 bg-white p-5" data-testid="config-historico">
        <h2 className="text-lg font-bold text-slate-900">Histórico</h2>
        <p className="mt-1 text-sm text-slate-500">
          Registro de todas as movimentações (entradas, saídas, devoluções e correções).
        </p>
        <button
          onClick={() => navigate("/historico")}
          data-testid="config-open-historico"
          className="mt-4 flex w-full items-center justify-between rounded-md border-2 border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          <span className="flex items-center gap-2">
            <History size={18} /> Abrir histórico
          </span>
          <ChevronRight size={18} />
        </button>
      </section>
      )}

      {/* Conta */}
      <section className="mt-6 rounded-md border-2 border-slate-200 bg-white p-5" data-testid="config-conta">
        <h2 className="text-lg font-bold text-slate-900">Sua conta</h2>
        <p className="mt-1 text-sm text-slate-500">
          {user?.is_master
            ? "Gerencie sua senha e os demais usuários do sistema."
            : "Gerencie sua senha."}
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <button
            onClick={() => navigate("/trocar-senha")}
            data-testid="config-open-trocar-senha"
            className="flex items-center justify-between rounded-md border-2 border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            <span className="flex items-center gap-2">
              <KeyRound size={18} /> Trocar minha senha
            </span>
            <ChevronRight size={18} />
          </button>
          {user?.is_master && (
            <button
              onClick={() => navigate("/usuarios")}
              data-testid="config-open-usuarios"
              className="flex items-center justify-between rounded-md border-2 border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              <span className="flex items-center gap-2">
                <Users size={18} /> Usuários
              </span>
              <ChevronRight size={18} />
            </button>
          )}
        </div>
      </section>

      {/* Limites de pedaço — só master */}
      {user?.is_master && (
      <section className="mt-6 rounded-md border-2 border-slate-200 bg-white p-5" data-testid="config-pedaco">
        <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
          <Scissors size={18} /> Limites de pedaço
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Um rolo é considerado <strong>pedaço</strong> quando o peso/metragem é <strong>menor</strong> que o limite
          abaixo. Deixe em branco para não classificar. A regra é aplicada em tempo real a todos os rolos —
          nenhum peso é alterado.
        </p>
        {loadingTipos ? (
          <div className="flex justify-center py-8 text-slate-400"><Loader2 className="animate-spin" /></div>
        ) : tiposPedaco.length === 0 ? (
          <div className="mt-4 rounded-md border-2 border-dashed border-slate-300 p-6 text-center text-sm text-slate-500" data-testid="config-pedaco-empty">
            Nenhum tipo de tecido cadastrado ainda. Cadastre em <strong>Cadastro</strong> primeiro.
          </div>
        ) : (
          <ul className="mt-4 divide-y divide-slate-100" data-testid="config-pedaco-list">
            {tiposPedaco.map((t) => (
              <li key={t.tipo} className="flex flex-wrap items-center gap-3 py-3" data-testid={`pedaco-row-${t.tipo}`}>
                <div className="min-w-[10rem] flex-1">
                  <div className="font-bold text-slate-900">{t.tipo}</div>
                  <div className="text-xs text-slate-500">
                    unidade: <strong>{t.unidade}</strong>
                    {t.updated_by_nome && <> · atualizado por {t.updated_by_nome}</>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="decimal"
                      className={`${inputCls} w-32 pr-10 font-mono`}
                      placeholder="—"
                      value={rascunhos[t.tipo] ?? ""}
                      onChange={(e) => setRascunhos((p) => ({ ...p, [t.tipo]: e.target.value }))}
                      data-testid={`pedaco-input-${t.tipo}`}
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-500">
                      {t.unidade}
                    </span>
                  </div>
                  <button
                    onClick={() => salvarLimite(t.tipo)}
                    disabled={savingTipo === t.tipo}
                    data-testid={`pedaco-save-${t.tipo}`}
                    className="flex items-center gap-1 rounded-md bg-slate-900 px-3 py-2 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-50"
                  >
                    {savingTipo === t.tipo ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Salvar
                  </button>
                  {t.limite != null && (
                    <button
                      onClick={() => limparLimite(t.tipo)}
                      disabled={savingTipo === t.tipo}
                      data-testid={`pedaco-clear-${t.tipo}`}
                      title="Remover limite"
                      className="flex items-center gap-1 rounded-md border-2 border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                    >
                      <X size={14} /> Limpar
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
      )}

      {/* Rendimento de gola/punho — cartão único, só master */}
      {user?.is_master && (
      <section className="mt-6 rounded-md border-2 border-slate-200 bg-white p-5" data-testid="config-rendimento-gola-punho">
        <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
          <Calculator size={18} /> Rendimento de gola e punho
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Informe quantas <strong>golas</strong> e/ou <strong>punhos</strong> rendem por kg em cada modo. Campos em branco significam que aquele modo não se aplica — não é erro.
        </p>

        {loadingRend ? (
          <div className="flex justify-center py-8 text-slate-400"><Loader2 className="animate-spin" /></div>
        ) : (
          <>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              {/* Juntos */}
              <div className="rounded-md bg-slate-50 p-3">
                <div className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-600">Gola e punho juntos</div>
                <div className="space-y-2">
                  <label className="block text-xs font-medium text-slate-500">
                    1 kg = <span className="text-slate-700">___ golas</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className={`${inputCls} mt-1 h-10 w-full font-mono`}
                      placeholder="—"
                      value={rendRasc.juntos_golas}
                      onChange={(e) => setRendRasc((p) => ({ ...p, juntos_golas: e.target.value }))}
                      data-testid="rend-juntos-golas"
                    />
                  </label>
                  <label className="block text-xs font-medium text-slate-500">
                    1 kg = <span className="text-slate-700">___ punhos</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className={`${inputCls} mt-1 h-10 w-full font-mono`}
                      placeholder="—"
                      value={rendRasc.juntos_punhos}
                      onChange={(e) => setRendRasc((p) => ({ ...p, juntos_punhos: e.target.value }))}
                      data-testid="rend-juntos-punhos"
                    />
                  </label>
                </div>
              </div>
              {/* Gola separado */}
              <div className="rounded-md bg-slate-50 p-3">
                <div className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-600">Gola separado</div>
                <label className="block text-xs font-medium text-slate-500">
                  1 kg = <span className="text-slate-700">___ golas</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    className={`${inputCls} mt-1 h-10 w-full font-mono`}
                    placeholder="—"
                    value={rendRasc.gola_sep}
                    onChange={(e) => setRendRasc((p) => ({ ...p, gola_sep: e.target.value }))}
                    data-testid="rend-gola-sep"
                  />
                </label>
              </div>
              {/* Punho separado */}
              <div className="rounded-md bg-slate-50 p-3">
                <div className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-600">Punho separado</div>
                <label className="block text-xs font-medium text-slate-500">
                  1 kg = <span className="text-slate-700">___ punhos</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    className={`${inputCls} mt-1 h-10 w-full font-mono`}
                    placeholder="—"
                    value={rendRasc.punho_sep}
                    onChange={(e) => setRendRasc((p) => ({ ...p, punho_sep: e.target.value }))}
                    data-testid="rend-punho-sep"
                  />
                </label>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
              <div className="text-xs text-slate-500">
                {rendMeta.updated_by_nome ? `Última atualização por ${rendMeta.updated_by_nome}` : "Ainda não salvo"}
              </div>
              <button
                onClick={salvarRendimento}
                disabled={savingRend}
                data-testid="rend-save"
                className="flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-50"
              >
                {savingRend ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Salvar rendimento
              </button>
            </div>
          </>
        )}
      </section>
      )}

      {/* Administração de dados — só master */}
      {user?.is_master && (
      <section className="mt-6 rounded-md border-2 border-slate-200 bg-white p-5" data-testid="config-dados">
        <h2 className="text-lg font-bold text-slate-900">Administração de dados</h2>
        <p className="mt-1 text-sm text-slate-500">
          Faça backups regulares. Limpe os dados só quando for começar do zero com dados reais.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <button
            onClick={() => navigate("/admin/backup")}
            data-testid="config-open-backup"
            className="flex items-center justify-between rounded-md border-2 border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            <span className="flex items-center gap-2">
              <Database size={18} /> Backup e restauração
            </span>
            <ChevronRight size={18} />
          </button>
          <button
            onClick={() => setLimparOpen(true)}
            data-testid="open-limpar"
            className="flex items-center justify-between rounded-md border-2 border-red-300 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 hover:bg-red-100"
          >
            <span className="flex items-center gap-2">
              <Trash2 size={18} /> Limpar dados de teste
            </span>
            <ChevronRight size={18} />
          </button>
        </div>
        <div className="mt-3 flex items-start gap-2 rounded-md bg-amber-50 p-3 text-xs text-amber-800">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>
            Ao limpar, o sistema salva automaticamente um backup do estado atual antes de apagar. Mantém seu login e os
            destinos.
          </span>
        </div>
      </section>
      )}

      {/* Confirmação limpar dados */}
      <AlertDialog open={limparOpen} onOpenChange={(o) => !o && setLimparOpen(false)}>
        <AlertDialogContent data-testid="limpar-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700">Limpar TODOS os dados de teste</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação é irreversível. Para confirmar, digite <strong>LIMPAR</strong> no campo abaixo. (Um backup
              automático é feito antes.)
            </AlertDialogDescription>
          </AlertDialogHeader>
          <input
            className={inputCls}
            value={limparTexto}
            onChange={(e) => setLimparTexto(e.target.value)}
            placeholder="Digite LIMPAR"
            data-testid="limpar-input"
          />
          <AlertDialogFooter>
            <button
              onClick={() => setLimparOpen(false)}
              className="rounded-md border-2 border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-100"
            >
              Cancelar
            </button>
            <button
              onClick={doLimpar}
              disabled={saving || limparTexto !== "LIMPAR"}
              data-testid="limpar-confirm"
              className="flex items-center gap-2 rounded-md bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-40"
            >
              {saving && <Loader2 className="animate-spin" size={16} />} Limpar tudo
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
